import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import CorporatePicker from "./CorporatePicker";
import "./InventoryLocationIncidentsAdmin.css";

const ENDPOINT = "/api/admin/inventory-location-incidents";
const TYPE_LABELS = { discrepancy: "Descuadre de inventario", unallocated: "Stock pendiente de ubicación" };
const quantity = (value) => {
  // PostgreSQL DECIMAL values arrive as strings; retain all eight inventory
  // decimals even when the integer part exceeds JavaScript's exact precision.
  const decimal = String(value ?? "0").match(/^(-?)(\d+)(?:\.(\d{1,8}))?$/);
  if (!decimal) return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 8 }).format(Number(value || 0));
  const fraction = (decimal[3] || "").replace(/0+$/, "");
  const digits = decimal[2].replace(/^0+(?=\d)/, "");
  const integer = digits.length > 4 ? digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : digits;
  const sign = decimal[1] && (digits !== "0" || fraction) ? "−" : "";
  return `${sign}${integer}${fraction ? `,${fraction}` : ""}`;
};
const dateTime = (value) => value && Number.isFinite(new Date(value).getTime())
  ? new Intl.DateTimeFormat("es-ES", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
  : "Sin comprobar";
const errorText = (error, fallback) => error?.response?.data?.error || error?.response?.data?.message || fallback;

function unitName(unit) {
  return typeof unit === "object" && unit !== null ? unit.name || unit.code || "—" : unit || "—";
}

export default function InventoryLocationIncidentsAdmin() {
  const isAdmin = useMemo(() => Number(readStoredUser()?.id) === 1, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const businessUnitId = /^[1-9]\d*$/.test(searchParams.get("business_unit_id") || "") ? searchParams.get("business_unit_id") : "";
  const type = ["discrepancy", "unallocated"].includes(searchParams.get("type")) ? searchParams.get("type") : "";
  const state = ["active", "resolved", "all"].includes(searchParams.get("state")) ? searchParams.get("state") : "active";
  const page = Math.max(1, Number.parseInt(searchParams.get("page"), 10) || 1);
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [notice, setNotice] = useState("");
  const [notificationAction, setNotificationAction] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [notificationError, setNotificationError] = useState("");
  const resolvingRef = useRef(false);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const settingsVersion = useRef(0);
  const queryKey = `${businessUnitId}:${type}:${state}:${page}`;

  useEffect(() => {
    if (!isAdmin) return undefined;
    let current = true;
    const controller = new AbortController();
    const version = settingsVersion.current;
    setLoading(true);
    setError("");
    axios.get(ENDPOINT, {
      params: { ...(businessUnitId ? { business_unit_id: Number(businessUnitId) } : {}), ...(type ? { type } : {}), state, page },
      signal: controller.signal,
    }).then(({ data: response }) => {
      if (!current) return;
      setData({ ...response, queryKey });
      if (!dirtyRef.current && !savingRef.current && version === settingsVersion.current) setDraft(response.settings);
    }).catch((requestError) => {
      if (current) setError(errorText(requestError, "No se pudieron cargar las incidencias. Vuelve a intentarlo."));
    }).finally(() => {
      if (current) setLoading(false);
    });
    return () => { current = false; controller.abort(); };
  }, [isAdmin, businessUnitId, type, state, page, queryKey, refresh]);

  const changeFilter = (key, value) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set(key, String(value)); else next.delete(key);
      if (key !== "page") next.delete("page");
      return next;
    });
  };

  const changeSetting = (key, value) => {
    dirtyRef.current = true;
    setNotice("");
    setDraft((previous) => ({ ...previous, [key]: value }));
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    if (savingRef.current || !draft) return;
    const interval = Number(draft.min_interval_hours);
    if (!Number.isInteger(interval) || interval < 1 || interval > 168) {
      setSettingsError("La frecuencia debe estar entre 1 y 168 horas enteras.");
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setSettingsError("");
    setNotice("");
    try {
      const response = await axios.patch(`${ENDPOINT}/settings`, { notifications_enabled: Boolean(draft.notifications_enabled), min_interval_hours: interval });
      settingsVersion.current += 1;
      setDraft(response.data.settings || response.data);
      dirtyRef.current = false;
      setNotice("Configuración guardada.");
    } catch (requestError) {
      setSettingsError(errorText(requestError, "No se pudo guardar la configuración. Puedes volver a intentarlo."));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const resolveNotification = async () => {
    if (!notificationAction || resolvingRef.current) return;
    resolvingRef.current = true;
    setResolving(true);
    setNotificationError("");
    try {
      await axios.post(`${ENDPOINT}/notifications/${notificationAction.id}/resolve`, { action: notificationAction.action, confirmed: true });
      setNotificationAction(null);
      setRefresh((value) => value + 1);
    } catch (requestError) {
      setNotificationError(errorText(requestError, "No se pudo resolver el aviso. Puedes volver a intentarlo."));
    } finally {
      resolvingRef.current = false;
      setResolving(false);
    }
  };

  if (!isAdmin) return <div className="ili-admin ili-access" role="alert">Solo el administrador principal puede consultar las incidencias de stock.</div>;

  const businessUnits = data?.business_units || [];
  const affectedBusinessUnits = businessUnits.filter((unit) => Number(unit.affected_items) > 0);
  const incidents = data?.queryKey === queryKey ? data.incidents || [] : [];
  const total = data?.queryKey === queryKey ? Number(data.total || 0) : 0;
  const pages = Math.max(1, Math.ceil(total / Number(data?.page_size || 25)));
  const monitor = data?.monitor;

  return (
    <main className="ili-admin">
      <header className="ili-header">
        <div><p className="ili-eyebrow">Administración · Inventario</p><h1>Incidencias de stock por ubicación</h1>
          <p>Supervisa descuadres y saldos pendientes de ubicación, sin modificar el inventario.</p></div>
        <button type="button" onClick={() => setRefresh((value) => value + 1)} disabled={loading}>Actualizar vista</button>
      </header>

      <section className="ili-explanation" aria-label="Tipos de incidencia">
        <p><strong>Descuadre:</strong> el stock global no coincide con físico + sin asignar.</p>
        <p><strong>Pendiente de ubicación:</strong> existe saldo sin asignar, positivo o negativo, incluso cuando la diferencia es cero.</p>
      </section>

      {data && <section className="ili-panel" aria-labelledby="ili-bu-title">
        <h2 id="ili-bu-title">BU con incidencias activas</h2>
        <p className="ili-muted">Recuento de artículos por BU. Un artículo puede tener ambos tipos de incidencia.</p>
        <div className="ili-business-units">
          {affectedBusinessUnits.map((unit) => <button type="button" className={`ili-bu${businessUnitId === String(unit.id) ? " ili-bu--selected" : ""}`} key={unit.id} onClick={() => { setSearchParams({ business_unit_id: String(unit.id), state: "active" }); }}>
            <strong>{unit.name} <small>#{unit.id}</small></strong>
            <span>{unit.affected_items} artículos afectados</span>
            <small>{unit.discrepancy_items} con descuadre · {unit.unallocated_items} pendientes de ubicación</small>
          </button>)}
          {!affectedBusinessUnits.length && <p>No hay BU con incidencias activas en la última comprobación.</p>}
        </div>
      </section>}

      <section className="ili-panel" aria-label="Listado de incidencias">
        <div className="ili-filters">
          <CorporatePicker label="Unidad de negocio" placeholder="Todas las BU autorizadas" value={businessUnitId} onChange={(value) => changeFilter("business_unit_id", value)} options={businessUnits.map((unit) => ({ value: String(unit.id), label: unit.name, description: `BU #${unit.id} · ${unit.affected_items || 0} artículos afectados` }))} />
          <label>Tipo de incidencia<select value={type} onChange={(event) => changeFilter("type", event.target.value)}><option value="">Todos los tipos</option><option value="discrepancy">Descuadre de inventario</option><option value="unallocated">Stock pendiente de ubicación</option></select></label>
          <label>Estado<select value={state} onChange={(event) => changeFilter("state", event.target.value)}><option value="active">Activas</option><option value="resolved">Resueltas</option><option value="all">Todos los estados</option></select></label>
        </div>
        {loading && <p role="status" className="ili-feedback">Cargando incidencias…</p>}
        {error && <div role="alert" className="ili-feedback ili-error">{error} <button type="button" onClick={() => setRefresh((value) => value + 1)} disabled={loading}>Reintentar</button></div>}
        {!loading && !error && !incidents.length && <p className="ili-feedback">No hay incidencias para los filtros seleccionados.</p>}
        {!loading && !error && incidents.length > 0 && <>
          <div className="ili-table-wrap"><table>
            <caption>Cantidades en la unidad de cada artículo; no se suman cantidades de artículos distintos.</caption>
            <thead><tr><th>BU</th><th>Artículo</th><th>Incidencia</th><th>Unidad</th><th>Global</th><th>Físico</th><th>Sin asignar</th><th>Diferencia</th><th>Estado y seguimiento</th></tr></thead>
            <tbody>{incidents.map((incident) => <tr key={incident.id}>
              <td>{incident.business_unit_name}<small>BU #{incident.business_unit_id}</small></td>
              <td><strong>{incident.item_name}</strong><small>{incident.item_code || `Artículo #${incident.item_id}`}</small></td>
              <td><span className={`ili-type ili-type--${incident.type}`}>{TYPE_LABELS[incident.type] || incident.type}</span></td>
              <td>{unitName(incident.unit)}</td>
              <td className="ili-qty">{quantity(incident.global_qty)}</td><td className="ili-qty">{quantity(incident.physical_qty)}</td><td className="ili-qty">{quantity(incident.unallocated_qty)}</td><td className="ili-qty">{quantity(incident.difference_qty)}</td>
              <td><span className={`ili-state ili-state--${incident.state}`}>{incident.state === "resolved" ? "Resuelta" : "Activa"}</span>
                <details><summary>Detalle de {incident.item_code || incident.item_name}</summary><dl>
                  <dt>Detectada</dt><dd>{dateTime(incident.detected_at)}</dd>
                  <dt>Última comprobación</dt><dd>{dateTime(incident.last_checked_at)}</dd>
                  {incident.resolved_at && <><dt>Resuelta</dt><dd>{dateTime(incident.resolved_at)}</dd></>}
                  <dt>Cálculo</dt><dd>Global − (físico + sin asignar)</dd>
                  {incident.source === "legacy_residual" && <><dt>Origen del saldo sin asignar</dt><dd>Saldo histórico sin libro por ubicación: diferencia entre global y físico.</dd></>}
                </dl></details>
              </td>
            </tr>)}</tbody>
          </table></div>
          <nav className="ili-pagination" aria-label="Páginas de incidencias"><span>{total} incidencias · Página {page} de {pages}</span><button type="button" disabled={page <= 1} onClick={() => changeFilter("page", page - 1)}>Anterior</button><button type="button" disabled={page >= pages} onClick={() => changeFilter("page", page + 1)}>Siguiente</button></nav>
        </>}
      </section>

      {monitor && <section className="ili-panel" aria-labelledby="ili-monitor-title"><h2 id="ili-monitor-title">Supervisión automática</h2>
        <p>Comprobación en segundo plano cada 15 minutos. Actualizar esta vista consulta los resultados guardados y no envía correos.</p>
        <p>Última comprobación: <strong>{dateTime(monitor.last_checked_at)}</strong> · Avisos pendientes: {monitor.pending_notifications || 0}</p>
        {monitor.last_error && <p role="alert" className="ili-error">Error de comprobación: {monitor.last_error}</p>}
        {monitor.notification_error && <p role="alert" className="ili-error">Error de notificación: {monitor.notification_error}</p>}
        {Number(monitor.uncertain_notifications) > 0 && <p className="ili-error">Hay {monitor.uncertain_notifications} avisos cuyo envío no pudo confirmarse. Comprueba el buzón antes de autorizar un reintento: el correo podría haber llegado.</p>}
        {(data.uncertain_notifications || []).map((notification) => <div key={notification.id} className="ili-uncertain">
          <strong>{notification.business_unit_name} · BU #{notification.business_unit_id}</strong>
          <p>{TYPE_LABELS[notification.type]} · Intento: {dateTime(notification.claimed_at)}</p>
          {notification.last_error && <p className="ili-muted">{notification.last_error}</p>}
          <div className="ili-notification-actions">
            <button type="button" disabled={resolving} onClick={() => { setNotificationError(""); setNotificationAction({ id: notification.id, action: "mark_sent" }); }}>Confirmar que llegó</button>
            <button type="button" disabled={resolving} onClick={() => { setNotificationError(""); setNotificationAction({ id: notification.id, action: "retry" }); }}>Autorizar reintento</button>
          </div>
          {notificationAction?.id === notification.id && <div className="ili-confirmation">
            <p>{notificationAction.action === "retry" ? "Confirma que has revisado el buzón. El próximo trabajo programado podrá reenviar este aviso; si el primer correo sí llegó, habrá un duplicado." : "Confirma que has recibido este aviso. Quedará registrado como enviado y no se volverá a enviar."}</p>
            <div className="ili-notification-actions"><button type="button" disabled={resolving} onClick={resolveNotification}>{resolving ? "Guardando decisión…" : "Confirmar decisión"}</button><button type="button" disabled={resolving} onClick={() => { setNotificationAction(null); setNotificationError(""); }}>Volver</button></div>
            {notificationError && <p role="alert" className="ili-error">{notificationError}</p>}
          </div>}
        </div>)}
      </section>}

      {draft && <section className="ili-panel" aria-labelledby="ili-settings-title"><h2 id="ili-settings-title">Notificaciones al administrador</h2>
        <p>Destinatario: {data?.recipient?.available ? <strong>{data.recipient.email}</strong> : <strong>Sin correo disponible</strong>} · Usuario #1</p>
        {data?.recipient?.available === false && <p role="alert" className="ili-error">El usuario 1 o su correo no están disponibles. Revisa su ficha en Administración; las incidencias se siguen registrando.</p>}
        <form onSubmit={saveSettings} className="ili-settings">
          <label className="ili-checkbox"><input type="checkbox" checked={Boolean(draft.notifications_enabled)} onChange={(event) => changeSetting("notifications_enabled", event.target.checked)} disabled={saving} /> Activar avisos por correo</label>
          <label>Intervalo mínimo entre avisos de una misma BU y tipo (horas)<input type="number" min="1" max="168" step="1" required value={draft.min_interval_hours} onChange={(event) => changeSetting("min_interval_hours", event.target.value)} disabled={saving} /></label>
          <p className="ili-muted">Primer aviso al detectar una incidencia. Los cambios relevantes y las reapariciones se agrupan por BU y tipo, respetando el intervalo. Si no hay cambios, no se repite el correo.</p>
          <button type="submit" disabled={saving}>{saving ? "Guardando…" : "Guardar configuración"}</button>
          {settingsError && <p role="alert" className="ili-error">{settingsError}</p>}{notice && <p role="status">{notice}</p>}
        </form>
      </section>}
    </main>
  );
}
